import React, { useState, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box, Button, Heading, Select, Table, Thead, Tbody, Tr, Th, Td,
  RadioGroup, Radio, Spinner, Stack,
   Text, HStack, Input, Center, TableContainer, FormControl, FormLabel, Link
} from "@chakra-ui/react";
import { DownloadIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

function CaseResultsReport() {
  const { accessToken } = useContext(AppContext);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dosDateFrom, setDosDateFrom] = useState('');
  const [dosDateTo, setDosDateTo] = useState('');
  const [previewLimit, setPreviewLimit] = useState(100);
  const [searchMode, setSearchMode] = useState('date');
  const [integrationSources, setIntegrationSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasGenerated, setHasGenerated] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      if (!accessToken) return;
      try {
        const response = await callApi('/api/clients', accessToken);
        const data = await response.json();
        if (response.ok) {
          setClients(data.clients);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClients();
  }, [accessToken]);

  useEffect(() => {
    const fetchIntegrationSources = async () => {
      if (!accessToken) return;
      try {
        const response = await callApi('/api/integration-sources', accessToken);
        const data = await response.json();
        if (response.ok) {
          setIntegrationSources(data.sources);
        }
      } catch (error) {
        console.error("Error fetching integration sources:", error);
      }
    };
    fetchIntegrationSources();
  }, [accessToken]);

  const handleGenerateReport = async () => {
    if (!accessToken) return;
    setLoading(true);
    setHasGenerated(true);
    try {
      const params = new URLSearchParams();
      
      if (searchMode === 'date') {
        if (selectedClient) params.append('client_id', selectedClient);
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        if (dosDateFrom) params.append('dos_date_from', dosDateFrom);
        if (dosDateTo) params.append('dos_date_to', dosDateTo);
      } else if (searchMode === 'source') {
        if (selectedSource) params.append('source_file', selectedSource);
      }

      params.append('limit', previewLimit);
      
      const url = `/api/case-results-report?${params.toString()}`;
      const response = await callApi(url, accessToken);
      const data = await response.json();

      if (response.ok) {
        setReportData(data.results);
        setTotalCount(data.total_count);
      } else {
        console.error("Failed to generate report:", data.message);
        setReportData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!accessToken) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      
      if (searchMode === 'date') {
        if (selectedClient) params.append('client_id', selectedClient);
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        if (dosDateFrom) params.append('dos_date_from', dosDateFrom);
        if (dosDateTo) params.append('dos_date_to', dosDateTo);
      } else if (searchMode === 'source') {
        if (selectedSource) params.append('source_file', selectedSource);
      }
      
      const url = `/api/export-case-results?${params.toString()}`;

      const response = await callApi(url, accessToken);
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'case_results.xlsx';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch.length > 1) {
                filename = filenameMatch[1];
            }
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Failed to download report");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={4}>
        <Heading mb={4}>Case Results Report</Heading>
        
        <Box mb={4}>
          <RadioGroup onChange={setSearchMode} value={searchMode}>
            <Stack direction='row' spacing={5}>
              <Radio value='date'>Search by Date Range</Radio>
              <Radio value='source'>Search by Integration Source</Radio>
            </Stack>
          </RadioGroup>
        </Box>

        <HStack spacing={4} mb={4} align="flex-end">
          {searchMode === 'date' ? (
            <>
              <FormControl>
                <FormLabel>Client (Optional)</FormLabel>
                <Select
                  placeholder="All Clients"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                >
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Coding Date From</FormLabel>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Coding Date To</FormLabel>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>DOS Date From</FormLabel>
                <Input
                  type="date"
                  value={dosDateFrom}
                  onChange={(e) => setDosDateFrom(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>DOS Date To</FormLabel>
                <Input
                  type="date"
                  value={dosDateTo}
                  onChange={(e) => setDosDateTo(e.target.value)}
                />
              </FormControl>
            </>
          ) : (
            <FormControl>
              <FormLabel>Integration Source</FormLabel>
              <Select
                placeholder="Select Source File"
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
              >
                {integrationSources.map((source, index) => (
                  <option key={index} value={source.name}>
                    {source.name} ({source.count} items)
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          
          <FormControl w="180px">
            <FormLabel>Preview Rows</FormLabel>
            <Input
              type="number"
              value={previewLimit}
              onChange={(e) => setPreviewLimit(e.target.value)}
            />
          </FormControl>
        </HStack>
        <HStack spacing={4} mb={4}>
          <Button
            colorScheme="blue"
            onClick={handleGenerateReport}
            isLoading={loading}
          >
            Generate Report
          </Button>
          <Button
            leftIcon={<DownloadIcon />}
            colorScheme="green"
            onClick={handleExport}
            isLoading={exporting}
            isDisabled={!hasGenerated || totalCount === 0}
          >
            Export Full Report
          </Button>
        </HStack>

        {loading ? (
          <Center><Spinner /></Center>
        ) : hasGenerated ? (
          <>
            <Text mb={2} fontWeight="bold">
              Showing {reportData.length} of {totalCount} total results.
            </Text>
            <TableContainer>
              <Table variant="simple" size="sm" tableLayout="fixed">
                <Thead>
                  <Tr>
                    <Th w="15%">Chart #</Th>
                    <Th w="12%">Date of Service</Th>
                    <Th w="12%">Coding Date</Th>
                    <Th w="18%">CPT</Th>
                    <Th w="20%">CPT Justification</Th>
                    <Th w="20%">Coder Notes</Th>
                    <Th w="18%">ICD</Th>
                    <Th w="10%">Confidence</Th>
                    <Th w="10%">Case Type</Th>
                    <Th w="10%">Payer</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {reportData.map((row, index) => (
                    <Tr key={index}>
                      <Td sx={{ wordBreak: 'break-word' }}>
                        <Link as={RouterLink} to={`/full-cpt-coder?caseId=${row.chart_number}`} color="blue.500">
                          {row.chart_number}
                        </Link>
                      </Td>
                      <Td>{row.dos}</Td>
                      <Td>{row.coding_date}</Td>
                      <Td sx={{ whiteSpace: 'normal', wordBreak: 'break-all', maxWidth: '250px' }}>{row.cpt_codes}</Td>
                      <Td sx={{ whiteSpace: 'normal', wordBreak: 'break-all', maxWidth: '300px' }}><ReactMarkdown>{row.justification}</ReactMarkdown></Td>
                      <Td sx={{ whiteSpace: 'normal', wordBreak: 'break-all', maxWidth: '300px' }}><ReactMarkdown>{row.coder_notes}</ReactMarkdown></Td>
                      <Td sx={{ whiteSpace: 'normal', wordBreak: 'break-all', maxWidth: '250px' }}>{row.icd_codes}</Td>
                      <Td>{row.confidence_score}</Td>
                      <Td sx={{ wordBreak: 'break-word' }}>{row.case_type}</Td>
                      <Td sx={{ wordBreak: 'break-word' }}>{row.payer}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Text>
            Select filters and click "Generate Report" to see the results.
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default CaseResultsReport;